
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ParkingCircle, Upload, MapPin, Shield, Tag, Images, File, HardDrive } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const ListSpot = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isApartment, setIsApartment] = useState("no");
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    vehicleType: "",
    fourWheelerType: "",
    securityType: "",
    pricePerHour: "",
    blockNumber: "",
    images: [] as File[],
    verificationDocType: "",
    verificationDoc: null as File | null,
    parkingLotCount: "1",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVerificationDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({
        ...prev,
        verificationDoc: e.target.files![0],
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData((prev) => ({
        ...prev,
        images: Array.from(e.target.files || []),
      }));
    }
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to list a parking spot.",
          variant: "destructive",
        });
        return;
      }

      // Upload verification document if provided
      let verificationDocUrl = null;
      if (formData.verificationDoc) {
        const fileName = `${Date.now()}-${formData.verificationDoc.name}`;
        const { data: verificationData, error: verificationError } = await supabase.storage
          .from("parking-spots")
          .upload(`verification/${fileName}`, formData.verificationDoc);

        if (verificationError) throw verificationError;
        if (verificationData) verificationDocUrl = verificationData.path;
      }

      // Upload parking spot images
      const imageUrls = [];
      for (const image of formData.images) {
        const fileName = `${Date.now()}-${image.name}`;
        const { data, error: uploadError } = await supabase.storage
          .from("parking-spots")
          .upload(fileName, image);
        if (uploadError) throw uploadError;
        if (data) imageUrls.push(data.path);
      }

      const finalVehicleType = formData.vehicleType === "4-wheeler" && formData.fourWheelerType 
        ? `${formData.vehicleType}-${formData.fourWheelerType}` 
        : formData.vehicleType;

      const address = isApartment === "yes" && formData.blockNumber
        ? `${formData.address} (Block ${formData.blockNumber})`
        : formData.address;

      const { error } = await supabase.from("parking_spots").insert([
        {
          owner_id: user.id,
          owner_name: formData.name,
          owner_email: formData.email,
          owner_phone: formData.phone,
          address: address,
          vehicle_type: finalVehicleType,
          security_type: formData.securityType,
          price_per_hour: parseFloat(formData.pricePerHour),
          images: imageUrls,
          verification_document_type: formData.verificationDocType,
          verification_document_url: verificationDocUrl,
          parking_lot_count: parseInt(formData.parkingLotCount),
        },
      ]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your parking spot has been listed successfully.",
      });
      navigate("/");
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to list parking spot. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="flex justify-center mb-8">
        <div className="flex items-center">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentStep === step 
                    ? "bg-primary text-white" 
                    : currentStep > step 
                    ? "bg-green-500 text-white" 
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {currentStep > step ? "✓" : step}
              </div>
              {step < 3 && (
                <div className={`w-12 h-1 ${
                  currentStep > step ? "bg-green-500" : "bg-gray-200"
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mr-4"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">List Your Parking Spot</h1>
      </div>
      
      {renderStepIndicator()}
      
      <div className="max-w-2xl mx-auto">
        <Card className="border shadow-md">
          <CardHeader className="bg-primary/5">
            <CardTitle className="flex items-center">
              {currentStep === 1 && (
                <>
                  <ParkingCircle className="h-6 w-6 mr-2" />
                  Personal Details
                </>
              )}
              {currentStep === 2 && (
                <>
                  <MapPin className="h-6 w-6 mr-2" /> 
                  Parking Space Details
                </>
              )}
              {currentStep === 3 && (
                <>
                  <Upload className="h-6 w-6 mr-2" />
                  Verification & Images
                </>
              )}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && "Tell us about yourself"}
              {currentStep === 2 && "Information about your parking space"}
              {currentStep === 3 && "Upload documents and images of your parking spot"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit}>
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-base">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="border-gray-300"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="border-gray-300"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-base">Phone Number</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="border-gray-300"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-base">Is this in an Apartment Complex?</Label>
                    <RadioGroup
                      value={isApartment}
                      onValueChange={(value) => setIsApartment(value)}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="apartment-yes" />
                        <Label htmlFor="apartment-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="apartment-no" />
                        <Label htmlFor="apartment-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {isApartment === "yes" && (
                    <div className="space-y-2">
                      <Label htmlFor="blockNumber" className="text-base">Block Number</Label>
                      <Input
                        id="blockNumber"
                        name="blockNumber"
                        value={formData.blockNumber}
                        onChange={handleInputChange}
                        placeholder="Enter block number"
                        required={isApartment === "yes"}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-base">Complete Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter full address of parking spot"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="parkingLotCount" className="text-base">Number of Parking Lots Available</Label>
                    <Input
                      type="number"
                      id="parkingLotCount"
                      name="parkingLotCount"
                      value={formData.parkingLotCount}
                      onChange={handleInputChange}
                      min="1"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pricePerHour" className="text-base flex items-center">
                      <Tag className="h-5 w-5 mr-2" />
                      Price per Hour (₹)
                    </Label>
                    <Input
                      type="number"
                      id="pricePerHour"
                      name="pricePerHour"
                      value={formData.pricePerHour}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="Enter hourly rate"
                    />
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-base flex items-center">
                      <HardDrive className="h-5 w-5 mr-2" />
                      Vehicle Type
                    </Label>
                    <RadioGroup
                      value={formData.vehicleType}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, vehicleType: value }))
                      }
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2-wheeler" id="two-wheeler" />
                        <Label htmlFor="two-wheeler">2 Wheeler</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="4-wheeler" id="four-wheeler" />
                        <Label htmlFor="four-wheeler">4 Wheeler</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {formData.vehicleType === "4-wheeler" && (
                    <div className="space-y-2 pl-6 border-l-2 border-gray-200">
                      <Label className="text-base">4 Wheeler Type</Label>
                      <RadioGroup
                        value={formData.fourWheelerType}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, fourWheelerType: value }))
                        }
                        className="grid grid-cols-2 gap-2"
                      >
                        {["Hatchback", "Sedan", "SUV", "MUV", "Coupe", "Convertible"].map((type) => (
                          <div key={type} className="flex items-center space-x-2">
                            <RadioGroupItem value={type.toLowerCase()} id={`type-${type.toLowerCase()}`} />
                            <Label htmlFor={`type-${type.toLowerCase()}`}>{type}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  )}

                  <div className="space-y-4">
                    <Label className="text-base flex items-center">
                      <Shield className="h-5 w-5 mr-2" />
                      Security Type
                    </Label>
                    <RadioGroup
                      value={formData.securityType}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, securityType: value }))
                      }
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="men" id="security-men" />
                        <Label htmlFor="security-men">Security Men</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="cctv" id="security-cctv" />
                        <Label htmlFor="security-cctv">CCTV</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="verificationDocType" className="text-base flex items-center">
                      <File className="h-5 w-5 mr-2" />
                      Verification Document Type
                    </Label>
                    <Select
                      value={formData.verificationDocType}
                      onValueChange={(value) => 
                        setFormData(prev => ({ ...prev, verificationDocType: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aadhar">Aadhar Card</SelectItem>
                        <SelectItem value="pan">PAN Card</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                        <SelectItem value="electricity_bill">Electricity Bill</SelectItem>
                        <SelectItem value="gas_bill">Gas Bill</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="verificationDoc" className="text-base">Upload Verification Document</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                      <Input
                        type="file"
                        id="verificationDoc"
                        onChange={handleVerificationDocChange}
                        accept="image/*,.pdf"
                        required
                        className="hidden"
                      />
                      <label htmlFor="verificationDoc" className="cursor-pointer">
                        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium">
                          {formData.verificationDoc 
                            ? formData.verificationDoc.name 
                            : "Click to upload document"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, or PNG (Max 5MB)
                        </p>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="images" className="text-base flex items-center">
                      <Images className="h-5 w-5 mr-2" />
                      Parking Spot Images
                    </Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                      <Input
                        type="file"
                        id="images"
                        name="images"
                        onChange={handleImageChange}
                        multiple
                        accept="image/*"
                        required
                        className="hidden"
                      />
                      <label htmlFor="images" className="cursor-pointer">
                        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium">
                          {formData.images.length > 0 
                            ? `${formData.images.length} images selected` 
                            : "Click to upload images"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Upload multiple photos of your parking space
                        </p>
                      </label>
                    </div>
                    {formData.images.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {Array.from(formData.images).map((image, index) => (
                          <div key={index} className="aspect-square bg-gray-100 rounded relative overflow-hidden">
                            <img 
                              src={URL.createObjectURL(image)} 
                              alt={`Preview ${index}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </form>
          </CardContent>
          <CardFooter className="flex justify-between border-t pt-6">
            {currentStep > 1 ? (
              <Button type="button" variant="outline" onClick={prevStep}>
                Previous
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button type="button" onClick={nextStep}>
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="bg-primary hover:bg-primary-700"
              >
                {loading ? "Listing..." : "List Parking Spot"}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ListSpot;
